import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { api, errMsg } from "@/lib/api";
import GoogleButton from "@/components/GoogleButton";
import Logo from "@/components/Logo";
import { ShieldCheck, KeyRound } from "lucide-react";

export default function Login() {
  const { login, refresh } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [otp, setOtp] = useState({ open: false, code: "", challenge_id: "", hint_email: "" });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
      if (data.requires_otp) {
        setOtp({ open: true, code: "", challenge_id: data.challenge_id, hint_email: data.hint_email });
        return;
      }
      if (data.token) localStorage.setItem("hk_token", data.token);
      await refresh();
      const u = data.user;
      if (u.role === "customer") navigate("/customer");
      else if (u.role === "merchant") navigate("/merchant");
      else if (u.role === "admin") navigate("/admin");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { challenge_id: otp.challenge_id, code: otp.code.trim() });
      if (data.token) localStorage.setItem("hk_token", data.token);
      await refresh();
      navigate("/admin");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  // Avoid unused login() import warning while preserving API
  void login;

  return (
    <div className="app-shell p-5 pb-24" data-testid="login-page">
      <div className="flex items-center justify-between">
        <Logo className="h-9 w-auto" testId="login-logo" />
        <Link to="/" className="text-sm text-zinc-500 hover:text-zinc-900" data-testid="login-back">{t("backHome")}</Link>
      </div>
      <h1 className="font-display text-3xl font-extrabold mt-6 text-[#12211D]">
        {otp.open ? t("twoFactorTitle") : t("welcome")}
      </h1>
      <p className="text-zinc-600 mt-1 text-sm">
        {otp.open ? <>{t("otpSentTo")} <b>{otp.hint_email}</b></> : t("login")}
      </p>

      {!otp.open ? (
        <form onSubmit={submit} className="mt-7 space-y-4">
          <div>
            <label className="label-caps mb-1.5 block">{t("email")}</label>
            <input
              data-testid="login-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full h-12 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-4"
            />
          </div>
          <div>
            <label className="label-caps mb-1.5 block">{t("password")}</label>
            <input
              data-testid="login-password"
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full h-12 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-4"
            />
          </div>
          {error && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 rounded-lg p-3">{error}</div>}
          <button data-testid="login-submit" disabled={busy} className="btn-primary w-full mt-2">
            {busy ? "..." : t("login")}
          </button>
        </form>
      ) : (
        <form onSubmit={verifyOtp} className="mt-7 space-y-4">
          <div className="card-surface p-4 flex items-start gap-3" data-testid="otp-info">
            <div className="h-9 w-9 rounded-full bg-emerald-50 text-emerald-700 grid place-items-center shrink-0">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="text-sm text-zinc-700">
              {t("otpCheckInbox")}
            </div>
          </div>
          <div>
            <label className="label-caps mb-1.5 block">{t("code")}</label>
            <input
              data-testid="otp-input"
              type="text"
              required
              inputMode="numeric"
              autoFocus
              autoComplete="one-time-code"
              maxLength={6}
              value={otp.code}
              onChange={(e) => setOtp({ ...otp, code: e.target.value.replace(/\D/g, "") })}
              className="w-full h-14 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-4 text-center text-2xl font-bold tracking-[0.5em]"
              placeholder="······"
            />
          </div>
          {error && <div data-testid="otp-error" className="text-sm text-red-600 bg-red-50 rounded-lg p-3">{error}</div>}
          <button data-testid="otp-submit" disabled={busy || otp.code.length < 4} className="btn-primary w-full">
            <KeyRound className="h-4 w-4" />
            {busy ? "..." : t("verifySignIn")}
          </button>
          <button
            type="button"
            data-testid="otp-cancel"
            onClick={() => { setOtp({ open: false, code: "", challenge_id: "", hint_email: "" }); setError(""); }}
            className="w-full text-sm text-zinc-500 hover:text-zinc-900"
          >
            {t("useDifferentAccount")}
          </button>
        </form>
      )}

      {!otp.open && (
        <>
          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-zinc-200" />
            <span className="text-xs text-zinc-400">{t("or")}</span>
            <div className="flex-1 h-px bg-zinc-200" />
          </div>
          <GoogleButton testId="login-google" />

          <p className="mt-6 text-center text-sm text-zinc-600">
            {t("dontHave")}{" "}
            <Link to="/signup" data-testid="link-signup" className="font-semibold text-emerald-700 hover:underline">{t("signup")}</Link>
          </p>
        </>
      )}
    </div>
  );
}
